import React from "react";
import { Link } from "react-router-dom";
import { Star, ArrowUpRight } from "lucide-react";
import { fmtPrice } from "../lib/api";
import { BuyButtonCard } from "./BuyButton";
import SafeImage from "./SafeImage";

const Stars = ({ rating = 4.8, count = 0 }) => (
    <div data-testid="product-rating" className="flex items-center gap-1.5">
        <div className="flex">
            {[1, 2, 3, 4, 5].map((i) => (
                <Star
                    key={i}
                    className={`w-3.5 h-3.5 ${
                        i <= Math.round(rating) ? "fill-brand-gold text-brand-gold" : "text-brand-border"
                    }`}
                    strokeWidth={1.5}
                />
            ))}
        </div>
        <span className="text-xs text-brand-inkSoft">{rating.toFixed(1)} {count > 0 && `(${count})`}</span>
    </div>
);

const ProductCard = ({ product }) => {
    if (!product) return null;
    const img = product.images?.[0];
    const hasDiscount = product.original_price && product.original_price > product.price;
    const badge = product.badges?.[0];

    return (
        <article
            data-testid={`product-card-${product.slug}`}
            className="bg-white border border-brand-border overflow-hidden group flex flex-col h-full transition-all duration-300 hover:shadow-[0_8px_40px_-12px_rgba(26,26,26,0.18)] hover:-translate-y-0.5"
        >
            <Link to={`/produtos/${product.slug}`} className="relative block aspect-square bg-brand-bg overflow-hidden">
                {badge && (
                    <span data-testid="product-badge" className="absolute top-3 left-3 z-10 bg-brand-ink text-white text-[10px] font-bold tracking-[0.15em] uppercase px-2.5 py-1 rounded-sm">
                        {badge}
                    </span>
                )}
                {hasDiscount && (
                    <span className="absolute top-3 right-3 z-10 bg-brand-gold text-white text-[10px] font-bold tracking-[0.1em] uppercase px-2.5 py-1 rounded-sm">
                        -{Math.round(((product.original_price - product.price) / product.original_price) * 100)}%
                    </span>
                )}
                <SafeImage
                    src={img}
                    alt={product.name}
                    loading="lazy"
                    className="object-cover w-full h-full transition-transform duration-700 ease-out group-hover:scale-105"
                    fallback={<div className="w-full h-full flex items-center justify-center bg-brand-bg text-brand-gold/40 text-4xl font-heading">{product.name?.[0]?.toUpperCase() || "V"}</div>}
                />
            </Link>
            <div className="p-4 sm:p-5 flex flex-col flex-grow">
                <div className="text-[10px] uppercase tracking-[0.2em] text-brand-gold font-bold mb-1.5" data-testid="product-category">
                    {product.category_slug}
                </div>
                <h3 className="font-heading font-semibold text-base text-brand-ink leading-snug mb-2 line-clamp-2">
                    <Link to={`/produtos/${product.slug}`} data-testid={`product-link-${product.slug}`} className="hover:text-brand-gold transition-colors">
                        {product.name}
                    </Link>
                </h3>
                <Stars rating={product.rating} count={product.reviews_count} />
                <div className="mt-3 flex items-baseline gap-2">
                    <span className="text-lg font-bold text-brand-ink" data-testid="product-price">{fmtPrice(product.price)}</span>
                    {hasDiscount && (
                        <span className="text-xs text-brand-inkSoft line-through">{fmtPrice(product.original_price)}</span>
                    )}
                </div>
                <div className="mt-auto pt-4 flex flex-col sm:flex-row gap-2">
                    <Link
                        to={`/produtos/${product.slug}`}
                        data-testid={`btn-details-${product.slug}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white transition-colors px-4 py-2.5 text-sm font-semibold rounded-sm"
                    >
                        Ver detalhes
                    </Link>
                    <BuyButtonCard product={product} />
                </div>
            </div>
        </article>
    );
};

export default ProductCard;
export { Stars };
